import {
    AlertTriangle, Calendar, CheckCircle2, Clock, Download, History,
    RefreshCw, TrendingDown, TrendingUp
} from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { AppealModal } from '../components/AppealModal';
import { MerchantPaymentAmounts } from '../components/MerchantPaymentAmounts';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Pagination } from '../components/ui/pagination';
import { useMerchantAuth } from '../contexts/MerchantAuthContext';
import { useExchangeRate } from '../hooks/useExchangeRate';
import type { MerchantPaymentListItem } from '../lib/api';
import { merchantAPI } from '../lib/api';
import { createMerchantPaymentsWorkbookFromSheet, merchantPaymentExportRow } from '../lib/merchantPaymentExport';
import { buildTransactionExportSheet } from '../lib/transactionExport';
import { resolveMerchantFiatCurrency } from '../lib/typicalV2';
import { formatAmount } from '../lib/utils';

import { MerchantPaymentSearch } from '../components/MerchantPaymentSearch';

type Payment = MerchantPaymentListItem;

export const MerchantPaymentHistoryPage: React.FC = () => {
    const { user } = useMerchantAuth();
    const fiatCurrency = resolveMerchantFiatCurrency(user);
    const { rate: exchangeRate } = useExchangeRate({ autoLoad: true });
    const [search, setSearch] = useState('');
    const requestId = useRef(0);
    const [payments, setPayments] = useState<Payment[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [overallTotals, setOverallTotals] = useState({
        deposit_count: 0,
        withdraw_count: 0,
        deposit_total: 0,
        withdraw_total: 0,
    });
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const exportingRef = useRef(false);
    const [exportProgress, setExportProgress] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [typeFilter, setTypeFilter] = useState<string>('all');
    const isTypicalIntegration = user?.integration_profile === 'typical_v2_2';

    // Состояние для модалки апелляции
    const [appealModalOpen, setAppealModalOpen] = useState(false);
    const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);

    const handleOpenAppeal = (payment: Payment) => {
        setSelectedPayment(payment);
        setAppealModalOpen(true);
    };

    // Проверка, старше ли транзакция 14 дней
    const isPaymentTooOld = (payment: Payment): boolean => {
        if (!payment.created_at) return false;
        const transactionDate = new Date(payment.created_at);
        const daysSinceCreation = (Date.now() - transactionDate.getTime()) / (1000 * 60 * 60 * 24);
        return daysSinceCreation > 14;
    };

    useEffect(() => {
        loadPayments();
        return () => { requestId.current += 1; };
    }, [currentPage, dateFrom, dateTo, statusFilter, typeFilter, search]);

    const filters = useMemo(() => ({
        search: search || undefined,
        start_date: dateFrom || undefined,
        end_date: dateTo || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        order_type: typeFilter !== 'all' ? typeFilter : undefined,
    }), [search, dateFrom, dateTo, statusFilter, typeFilter]);

    const loadPayments = async (page: number = currentPage) => {
        const currentRequest = ++requestId.current;
        try {
            setLoading(true);
            setError(null);

            const response = await merchantAPI.getPayments(page, pageSize, filters);
            if (currentRequest !== requestId.current) return;
            setPayments(response.payments || []);
            setTotalPages(response.pages || 1);
            setTotalItems(response.total || 0);
            setCurrentPage(response.page || page);

            const backendTotals = response.totals || {};
            setOverallTotals({
                deposit_count: backendTotals.deposit_count || 0,
                withdraw_count: backendTotals.withdraw_count || 0,
                deposit_total: backendTotals.deposit_total || 0,
                withdraw_total: backendTotals.withdraw_total || 0,
            });
        } catch (err: any) {
            if (currentRequest !== requestId.current) return;
            setError('Не удалось загрузить историю платежей');
            console.error('Ошибка загрузки платежей:', err);
        } finally {
            if (currentRequest === requestId.current) setLoading(false);
        }
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const exportToExcel = async () => {
        if (exportingRef.current) return;
        if (dateFrom && dateTo && dateFrom > dateTo) {
            setError('Дата начала периода не может быть позже даты окончания.');
            return;
        }
        exportingRef.current = true;
        setExporting(true);
        setExportProgress('Загрузка…');
        setError(null);
        try {
            const sheet = await buildTransactionExportSheet<Payment>(
                async (page, size) => {
                    const response = await merchantAPI.getPayments(page, size, filters);
                    return { items: response.payments, total: response.total };
                },
                merchantPaymentExportRow,
                (loaded, total) => setExportProgress(`${loaded} / ${total}`),
                { pageSize: 100, getId: payment => payment.transaction_id },
            );
            const wb = createMerchantPaymentsWorkbookFromSheet(sheet);
            const fileName = `payments_${new Date().toISOString().split('T')[0]}.xlsx`;
            XLSX.writeFile(wb, fileName);
        } catch (error) {
            setError(`Не удалось обновить данные для выгрузки. ${error instanceof Error ? error.message : 'Повторите попытку.'}`);
        } finally {
            exportingRef.current = false;
            setExporting(false);
            setExportProgress('');
        }
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'assigned':
                return <Badge variant="secondary">Назначен</Badge>;
            case 'pending_requisites':
                return <Badge variant="outline" className="text-yellow-700 dark:text-yellow-300 border-yellow-400 bg-yellow-50 dark:bg-yellow-950">Ожидает реквизитов</Badge>;
            case 'receipt_uploaded':
                return <Badge variant="default">Чек загружен</Badge>;
            case 'confirmed':
                return <Badge className="bg-blue-600">Подтвержден</Badge>;
            case 'completed':
                return <Badge className="bg-green-600">Завершен</Badge>;
            case 'cancelled':
                return <Badge variant="outline" className="text-red-600 dark:text-red-300">Отменен</Badge>;
            case 'disputed':
                return <Badge variant="outline" className="border-orange-300 bg-orange-50 text-orange-700 dark:border-orange-800 dark:bg-orange-950 dark:text-orange-300">Спор</Badge>;
            case 'no_traders':
                return <Badge variant="outline" className="text-gray-500 dark:text-slate-400 border-gray-300 dark:border-slate-700 bg-gray-50 dark:bg-slate-900">Нет трейдеров</Badge>;
            default:
                return <Badge variant="outline">{status}</Badge>;
        }
    };

    const getStatusLabel = (status: string): string => {
        const labels: Record<string, string> = {
            'assigned': 'Назначен',
            'receipt_uploaded': 'Чек загружен',
            'confirmed': 'Подтвержден',
            'completed': 'Завершен',
            'cancelled': 'Отменен'
        };
        return labels[status] || status;
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString('ru-RU', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const calculateProcessingTime = (createdAt: string, completedAt?: string) => {
        if (!completedAt) return null;
        const created = new Date(createdAt);
        const completed = new Date(completedAt);
        const diffMinutes = Math.round((completed.getTime() - created.getTime()) / 60000);
        return diffMinutes;
    };

    // Используем балансы из user (в USDT) и конвертируем в валюту мерчанта.
    // Это те же самые балансы, что отображаются в MerchantDashboardPage
    const rate = user?.exchange_rate || exchangeRate || (fiatCurrency === 'RUB' ? 100 : 0);
    const formatBalanceTotal = (amount: number) => rate > 0
        ? formatAmount(amount, fiatCurrency)
        : 'Курс недоступен';
    const depositTotalRub = (user?.deposit_balance || 0) * rate;
    const withdrawTotalRub = (user?.withdraw_balance || 0) * rate;

    const totals = {
        deposit_count: overallTotals.deposit_count,
        withdraw_count: overallTotals.withdraw_count,
        deposit_total: depositTotalRub,
        withdraw_total: withdrawTotalRub,
    };

    return (
        <div className="min-w-0">
            <div>
                <div className="space-y-6">
                    {/* Фильтры */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex flex-wrap items-center gap-2">
                                <Calendar className="h-5 w-5" />
                                Фильтры
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="mb-4">
                                <MerchantPaymentSearch value={search} onSearch={value => {
                                    setSearch(value);
                                    setCurrentPage(1);
                                }} />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="dateFrom">Дата от</Label>
                                    <Input
                                        id="dateFrom"
                                        type="date"
                                        value={dateFrom}
                                        onChange={(e) => { setDateFrom(e.target.value); setCurrentPage(1); }}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="dateTo">Дата до</Label>
                                    <Input
                                        id="dateTo"
                                        type="date"
                                        value={dateTo}
                                        onChange={(e) => { setDateTo(e.target.value); setCurrentPage(1); }}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="statusFilter">Статус</Label>
                                    <select
                                        id="statusFilter"
                                        value={statusFilter}
                                        onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-md"
                                    >
                                        <option value="all">Все</option>
                                        <option value="assigned">Назначен</option>
                                        <option value="completed">Завершен</option>
                                        <option value="cancelled">Отменен</option>
                                        <option value="disputed">Спор</option>
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="typeFilter">Тип</Label>
                                    <select
                                        id="typeFilter"
                                        value={typeFilter}
                                        onChange={(e) => { setTypeFilter(e.target.value); setCurrentPage(1); }}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-md"
                                    >
                                        <option value="all">Все</option>
                                        <option value="deposit">Пополнение</option>
                                        <option value="withdraw">Вывод</option>
                                    </select>
                                </div>
                            </div>
                            <div className="flex flex-wrap gap-2 mt-4">
                                <Button
                                    variant="outline"
                                    onClick={() => {
                                        setSearch('');
                                        setCurrentPage(1);
                                        setDateFrom('');
                                        setDateTo('');
                                        setStatusFilter('all');
                                        setTypeFilter('all');
                                    }}
                                >
                                    Сбросить фильтры
                                </Button>
                                <Button
                                    variant="default"
                                    onClick={exportToExcel}
                                    disabled={exporting || loading || payments.length === 0}
                                    className="bg-green-600 hover:bg-green-700"
                                >
                                    <Download className="h-4 w-4 mr-2" />
                                    {exporting ? `Экспорт: ${exportProgress}` : 'Экспорт в Excel'}
                                </Button>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Статистика */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <Card>
                            <CardContent className="p-4">
                                <div className="flex min-w-0 items-center gap-3">
                                    <div className="p-2 bg-blue-100 dark:bg-blue-950 rounded-lg">
                                        <History className="h-5 w-5 text-blue-600 dark:text-blue-300" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-600 dark:text-slate-300">Всего</p>
                                        <p className="text-xl font-bold">{totalItems}</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="p-4">
                                <div className="flex min-w-0 items-center gap-3">
                                    <div className="p-2 bg-green-100 dark:bg-green-950 rounded-lg">
                                        <TrendingUp className="h-5 w-5 text-green-600 dark:text-green-300" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-600 dark:text-slate-300">Пополнения</p>
                                        <p className="text-xl font-bold">{totals.deposit_count}</p>
                                        <p className="text-xs font-medium text-green-600 dark:text-green-300">{formatBalanceTotal(totals.deposit_total)}</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="p-4">
                                <div className="flex min-w-0 items-center gap-3">
                                    <div className="p-2 bg-red-100 dark:bg-red-950 rounded-lg">
                                        <TrendingDown className="h-5 w-5 text-red-600 dark:text-red-300" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-600 dark:text-slate-300">Выводы</p>
                                        <p className="text-xl font-bold">{totals.withdraw_count}</p>
                                        <p className="text-xs font-medium text-red-600 dark:text-red-300">{formatBalanceTotal(totals.withdraw_total)}</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent className="p-4">
                                <div className="flex min-w-0 items-center gap-3">
                                    <div className="p-2 bg-purple-100 dark:bg-purple-950 rounded-lg">
                                        <CheckCircle2 className="h-5 w-5 text-purple-600 dark:text-purple-300" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-600 dark:text-slate-300">Чистый баланс</p>
                                        <p className="text-xl font-bold">{formatBalanceTotal(totals.deposit_total - totals.withdraw_total)}</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Таблица платежей */}
                    <Card>
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <CardTitle>Платежи ({payments.length})</CardTitle>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => loadPayments(currentPage)}
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <RefreshCw className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <RefreshCw className="h-4 w-4" />
                                    )}
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {error && (
                                <div className="p-4 text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-md mb-4">
                                    {error}
                                </div>
                            )}

                            {loading ? (
                                <div className="text-center py-12">
                                    <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-gray-400" />
                                    <p className="text-gray-500 dark:text-slate-400">Загрузка платежей...</p>
                                </div>
                            ) : payments.length === 0 ? (
                                <div className="text-center py-12 text-gray-500 dark:text-slate-400">
                                    <History className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                                    <p>Платежи не найдены</p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {payments.map((payment) => {
                                        const processingTime = calculateProcessingTime(payment.created_at, payment.completed_at);
                                        return (
                                            <div
                                                key={payment.transaction_id}
                                                className="border border-gray-200 dark:border-slate-700 rounded-lg p-4 hover:shadow-md transition-shadow group"
                                            >
                                                <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                                                    <div className="flex min-w-0 items-center gap-3">
                                                        <div className="text-2xl">
                                                            {payment.order_type === 'deposit' ? '📥' : '📤'}
                                                        </div>
                                                        <div>
                                                            <div className="flex flex-wrap items-center gap-2">
                                                                <span className="font-medium">
                                                                    {payment.merchantOrderId || payment.merchantPaymentId || payment.merchantWithdrawalId || payment.payment_id}
                                                                </span>
                                                                {getStatusBadge(payment.status)}
                                                            </div>
                                                            <div className="text-sm text-gray-500 dark:text-slate-400 space-y-0.5">
                                                                <div>Client ID: {payment.user_id || '-'}</div>
                                                                <div>System Payment ID: {payment.payment_id}</div>
                                                                <div>Transaction ID: {payment.transaction_id}</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {!isTypicalIntegration && <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleOpenAppeal(payment)}
                                                        disabled={isPaymentTooOld(payment)}
                                                        className={`transition-colors ${isPaymentTooOld(payment)
                                                            ? 'text-gray-400 cursor-not-allowed'
                                                            : 'text-orange-600 dark:text-orange-300 hover:text-orange-700 dark:hover:text-orange-300 hover:bg-orange-50 dark:hover:bg-orange-950'
                                                            }`}
                                                        title={isPaymentTooOld(payment) ? 'Невозможно открыть спор: транзакция старше 14 дней' : 'Открыть спор'}
                                                    >
                                                        <AlertTriangle className="h-4 w-4 mr-1" />
                                                        Спор
                                                    </Button>}
                                                </div>

                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                                                    <div>
                                                        <p className="text-gray-600 dark:text-slate-300 mb-1">💰 Суммы</p>
                                                        <MerchantPaymentAmounts payment={payment} />
                                                    </div>

                                                    <div>
                                                        <p className="text-gray-600 dark:text-slate-300 mb-1">📅 Временная линия</p>
                                                        <div className="space-y-1">
                                                            <p className="text-xs">
                                                                <Clock className="h-3 w-3 inline mr-1" />
                                                                Создан: {formatDate(payment.created_at)}
                                                            </p>
                                                            {payment.completed_at && (
                                                                <p className="text-xs text-green-600 dark:text-green-300">
                                                                    <CheckCircle2 className="h-3 w-3 inline mr-1" />
                                                                    Завершен: {formatDate(payment.completed_at)}
                                                                </p>
                                                            )}
                                                            {processingTime !== null && (
                                                                <p className="text-xs text-gray-500 dark:text-slate-400">
                                                                    ⏱️ Обработка: {processingTime} мин
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div>
                                                        <p className="text-gray-600 dark:text-slate-300 mb-1">ℹ️ Дополнительно</p>
                                                        <div className="space-y-1">
                                                            <p className="text-xs">
                                                                Тип: {payment.order_type === 'deposit' ? 'Пополнение' : 'Вывод'}
                                                            </p>
                                                            {payment.confirmed_at && (
                                                                <p className="text-xs text-gray-500 dark:text-slate-400">
                                                                    Подтвержден: {formatDate(payment.confirmed_at)}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}

                                    <Pagination
                                        currentPage={currentPage}
                                        totalPages={totalPages}
                                        onPageChange={handlePageChange}
                                        totalItems={totalItems}
                                        pageSize={pageSize}
                                    />
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>

            <AppealModal
                open={appealModalOpen}
                onOpenChange={setAppealModalOpen}
                payment={selectedPayment}
                apiKey={user?.api_key || ''}
            />
        </div>
    );
};
